// Structural element IDs only. Text, input values, URL queries and pointer coordinates never leave the browser.
import { captureVisitorAcquisition } from './visitorAcquisition.js';
const STORAGE = 'lawlow.behavior.v1';
const VISITOR_SESSION = 'lawlow.behavior.session';
const SELECTOR = '[data-behavior],h1,h2,h3,p,section,article,fieldset,nav,summary,button,a,textarea,input,[role="dialog"]';
const id = () => Array.from(crypto.getRandomValues(new Uint8Array(16)), b => b.toString(16).padStart(2, '0')).join('');
export const behaviorPage = path => {
  if (path === '/termsofservice') return '/terms';
  if (path === '/privacypolicy') return '/privacy';
  if (/^\/main\/q\//.test(path)) return '/main/q';
  return ['/', '/onboarding', '/main', '/lab', '/lab/rehearsal', '/lab/evidence', '/app-guide', '/termsofservice', '/privacypolicy'].includes(path) ? path : '/other';
};
export function behaviorElement(node) {
  const declared = node.closest('[data-behavior]');
  const parts = [];
  let current = node;
  while (current && current !== declared && current !== document.body && parts.length < 12) {
    const tag = current.tagName;
    const siblings = current.parentElement ? Array.from(current.parentElement.children).filter(x => x.tagName === tag) : [];
    parts.unshift(current.tagName.toLowerCase() + (siblings.indexOf(current) + 1));
    current = current.parentElement;
  }
  let hash = 2166136261;
  for (const char of parts.join('.')) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619);
  const prefix = declared?.getAttribute('data-behavior');
  const safe = /^[a-z][a-z0-9_.:-]{0,63}$/.test(prefix || '') ? prefix : 'auto';
  return node === declared ? safe : `${safe}.${node.tagName.toLowerCase()}.${(hash >>> 0).toString(36)}`;
}
function stored(key, fallback, storage = sessionStorage) { try { return JSON.parse(storage.getItem(key)) || fallback; } catch { return fallback; } }
function save(key, value, storage = sessionStorage) { try { storage.setItem(key, JSON.stringify(value)); } catch { /* memory remains usable */ } }

export function startBehaviorAnalytics(endpoint, root = document.body) {
  if (!endpoint || !root || typeof IntersectionObserver === 'undefined' || /^\/oauth\//.test(window.location.pathname)) return () => {};
  let session = stored(VISITOR_SESSION, null);
  if (!session || !/^[a-f0-9]{32}$/.test(session.id) || Date.now() - session.last > 30 * 60 * 1000) {
    session = { id: id(), sequence: 0, last: Date.now(), internal: new URLSearchParams(window.location.search).get('internal_test') === '1' };
  }
  if (!session.acquisition) session.acquisition = captureVisitorAcquisition();
  session.acquisition.landingPath = behaviorPage(session.acquisition.landingPath);
  let queueKey = STORAGE + '.' + session.id;
  const backlog = stored(queueKey, {events: []}, localStorage);
  let queue = (Array.isArray(backlog.events) ? backlog.events : []).filter(e => e && /^[a-f0-9]{32}$/.test(e.eventId) && Date.now() - Date.parse(e.occurredAt) < 24 * 3600000);
  let active = true, initialized = false, initializing = false, sending = false, recovering = false;
  let page = behaviorPage(window.location.pathname), variant = '', pageHidden = document.visibilityState !== 'visible';
  const seen = new WeakSet(), observed = new Map();
  const persist = () => { save(queueKey, {session, events: queue}, localStorage); save(VISITOR_SESSION, session); };
  let persistencePending = false;
  const schedulePersist = () => {
    if (persistencePending) return;
    persistencePending = true;
    Promise.resolve().then(() => { persistencePending = false; if (active) persist(); });
  };
  const emit = (kind, element = '', durationMs = 0) => {
    if (!active) return;
    session.last = Date.now(); session.sequence += 1;
    queue.push({ eventId: id(), sequence: session.sequence, page, element, kind, durationMs, occurredAt: new Date().toISOString(), variant });
    // Bound one tab's durable retry backlog; its gap is visible through sequence discontinuities.
    if (queue.length > 2000) queue = queue.slice(-2000);
    schedulePersist();
  };
  async function initialize() {
    if (initialized || initializing || !active) return;
    initializing = true;
    try {
      const response = await fetch(endpoint + '/public/behavior/session', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sessionId: session.id, internal: session.internal, width: window.innerWidth, height: window.innerHeight, acquisition: session.acquisition }) });
      if (response.status === 409) {
        // A deleted visitor cookie cannot claim an earlier browser's session. Start a new visit.
        session = {id:id(), sequence:0, last:Date.now(), internal:session.internal, acquisition:session.acquisition};
        queueKey = STORAGE + '.' + session.id; queue = []; persist();
        emit('page_view'); for (const state of observed.values()) emit('loaded',state.key);
      }
      if (response.ok) { const result = await response.json(); initialized = result.sessionId === session.id; }
    } catch { /* collection failure never blocks product interaction */ }
    finally { initializing = false; }
  }
  async function flush(beacon = false) {
    if (beacon) persist();
    if (!initialized) { await initialize(); if (!initialized) return; }
    if (!queue.length || sending) return;
    const batch = queue.slice(0, 40);
    const body = JSON.stringify({ sessionId: session.id, events: batch });
    if (beacon && navigator.sendBeacon) {
      // A true return means queued, not stored. Keep the same IDs until a later HTTP ack.
      navigator.sendBeacon(endpoint + '/public/behavior/events', new Blob([body], { type: 'application/json' }));
      return;
    }
    sending = true;
    let acknowledged = false;
    try {
      const response = await fetch(endpoint + '/public/behavior/events', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body, keepalive: true });
      if (response.status === 204 && active) { const ack = new Set(batch.map(e => e.eventId)); queue = queue.filter(e => !ack.has(e.eventId)); persist(); acknowledged = true; }
    } catch { /* retry same event IDs after reconnect */ }
    finally { sending = false; }
    if (acknowledged && active && queue.length) void flush();
  }
  const accrue = state => {
    if (!state.visible || pageHidden) return;
    const now = performance.now(), duration = Math.min(10000, Math.max(0, Math.round(now - state.last)));
    state.last = now;
    if (duration) emit('dwell', state.key, duration);
  };
  const stop = state => {
    clearTimeout(state.timer);
    if (state.visible) { accrue(state); state.visible = false; emit('hidden', state.key); }
    state.intersecting = false;
  };
  const begin = state => {
    clearTimeout(state.timer);
    if (state.visible || pageHidden || !state.intersecting) return;
    state.timer = setTimeout(() => {
      if (!active || pageHidden || !state.intersecting || !state.node.isConnected) return;
      state.visible = true; state.last = performance.now() - 1000; emit('visible', state.key);
    }, 1000);
  };
  const observer = new IntersectionObserver(entries => {
    for (const entry of entries) {
      const state = observed.get(entry.target); if (!state) continue;
      const area = entry.boundingClientRect.width * entry.boundingClientRect.height;
      const viewport = window.innerWidth * window.innerHeight;
      const threshold = Math.min(0.5, viewport * 0.3 / Math.max(1, area));
      if (entry.isIntersecting && entry.intersectionRatio >= threshold) { state.intersecting = true; begin(state); }
      else stop(state);
    }
  }, { threshold: [0, 0.01, 0.05, 0.1, 0.25, 0.5, 1] });
  function register() {
    variant = document.querySelector('[data-landing-variant]')?.getAttribute('data-landing-variant') || '';
    for (const node of root.querySelectorAll(SELECTOR)) {
      if (seen.has(node) || observed.size >= 600 || node.closest('[data-behavior-ignore]')) continue;
      if (node instanceof HTMLInputElement && ['hidden', 'password', 'file'].includes(node.type)) continue;
      seen.add(node); const state = { node, key: behaviorElement(node), visible: false, intersecting: false, timer: null, last: 0 };
      observed.set(node, state); emit('loaded', state.key); observer.observe(node);
    }
    for (const [node, state] of observed) if (!node.isConnected) { stop(state); observer.unobserve(node); observed.delete(node); }
  }
  let mutationTimer;
  const mutations = new MutationObserver(() => { clearTimeout(mutationTimer); mutationTimer = setTimeout(register, 80); });
  mutations.observe(root, { childList: true, subtree: true });
  const clicked = event => {
    const target = event.target.closest?.('button,a,summary,input,[data-behavior]');
    if (target && root.contains(target) && !['password','hidden','file'].includes(target.type) && !target.closest('[data-behavior-ignore]')) emit('click', behaviorElement(target));
  };
  const focused = event => { if (root.contains(event.target) && !event.target.closest('[data-behavior-ignore]') && !['password','hidden','file'].includes(event.target.type)) emit('focus', behaviorElement(event.target)); };
  const visibility = () => {
    if (document.visibilityState !== 'visible') {
      for (const state of observed.values()) accrue(state);
      pageHidden = true; for (const state of observed.values()) { clearTimeout(state.timer); state.visible = false; }
      emit('page_hidden'); void flush(true);
    } else { pageHidden = false; emit('page_resume'); for (const state of observed.values()) begin(state); void flush(); }
  };
  const pagehide = () => { for (const state of observed.values()) accrue(state); pageHidden = true; for (const state of observed.values()) { clearTimeout(state.timer); state.visible = false; } emit('page_exit'); void flush(true); };
  const pageshow = () => { pageHidden = document.visibilityState !== 'visible'; emit('page_resume'); for (const state of observed.values()) begin(state); void flush(); };
  const reconnect = () => { void flush(); void recover(); };
  async function recover() {
    if (!initialized || recovering || !active) return;
    // Each tab owns its own key. Recover unacknowledged events after a tab was closed.
    let keys; try { keys = Object.keys(localStorage).filter(key => key.startsWith(STORAGE + '.') && key !== queueKey); } catch { return; }
    recovering = true;
    try { for (const key of keys) {
      const saved = stored(key, null, localStorage);
      if (!saved?.session || !Array.isArray(saved.events)) continue;
      const events = saved.events.filter(e => Date.now() - Date.parse(e.occurredAt) < 24 * 3600000);
      if (!events.length) { try { localStorage.removeItem(key); } catch {} continue; }
      // Do not drain another live tab's backlog. After 30 seconds of silence it is safe to retry identical IDs.
      if (Date.now() - saved.session.last < 30000) continue;
      try {
        const started = await fetch(endpoint + '/public/behavior/session', { method: 'POST', credentials: 'include', headers: {'Content-Type':'application/json'}, body: JSON.stringify({sessionId:saved.session.id,internal:saved.session.internal,width:0,height:0}) });
        if (!started.ok) continue;
        const batch = events.slice(0,40);
        const response = await fetch(endpoint + '/public/behavior/events', {method:'POST',credentials:'include',headers:{'Content-Type':'application/json'},body:JSON.stringify({sessionId:saved.session.id,events:batch})});
        if (response.status === 204) {
          const current = stored(key, saved, localStorage), ack = new Set(batch.map(e=>e.eventId));
          current.events = current.events.filter(e=>!ack.has(e.eventId));
          save(key,current,localStorage);
        }
      } catch { /* leave the same IDs for the next attempt */ }
    } } finally { recovering = false; }
  }
  root.addEventListener('click', clicked, true); root.addEventListener('focusin', focused, true);
  document.addEventListener('visibilitychange', visibility); window.addEventListener('pagehide', pagehide); window.addEventListener('pageshow', pageshow); window.addEventListener('online', reconnect);
  emit('page_view'); register(); void initialize().then(() => { void flush(); void recover(); });
  const timer = setInterval(() => { for (const state of observed.values()) accrue(state); if (!pageHidden) emit('heartbeat'); void flush(); void recover(); }, 10000);
  return () => {
    for (const state of observed.values()) stop(state);
    emit('page_exit'); persist(); void flush(); active = false;
    observer.disconnect(); mutations.disconnect(); clearTimeout(mutationTimer); clearInterval(timer);
    root.removeEventListener('click', clicked, true); root.removeEventListener('focusin', focused, true);
    document.removeEventListener('visibilitychange', visibility); window.removeEventListener('pagehide', pagehide); window.removeEventListener('pageshow', pageshow); window.removeEventListener('online', reconnect);
  };
}
